import { CodeNode } from '@lexical/code';
import { LinkNode } from '@lexical/link';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
} from '@lexical/list';
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  type MultilineElementTransformer,
  TRANSFORMERS,
} from '@lexical/markdown';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { $createHeadingNode, HeadingNode, QuoteNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $setSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  type RangeSelection,
  UNDO_COMMAND,
} from 'lexical';
import {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { PatientDocumentImage } from '@/shared/api/protocol-ts/model/dto_patient_document_pb';
import { Icon } from '@/shared/ui';

import { patientDocumentMessages } from './config';
import {
  $createDocumentImageNode,
  $isDocumentImageNode,
  DOCUMENT_IMAGE_TRANSFORMER,
  DocumentAssetContext,
  DocumentImageNode,
} from './DocumentImageNode';
import styles from './MarkdownWysiwygEditor.module.scss';

type InsertImage = (serial: number, onInserted?: () => void) => void;
type EditSelection = (text: string) => boolean;

type Props = {
  title: string;
  onTitleChange: (title: string) => void;
  value: string;
  documentID: string;
  images: PatientDocumentImage[];
  resolveAsset: (assetID: string) => string;
  insertImageRef: MutableRefObject<InsertImage | null>;
  replaceSelectionRef: MutableRefObject<EditSelection | null>;
  insertBelowSelectionRef: MutableRefObject<EditSelection | null>;
  onSelectionChange: (text: string) => void;
  onChange: (markdown: string) => void;
  onInsertImage: () => void;
  onUploadImage: () => void;
  onUpdateImage?: (serial: number, caption: string) => void | Promise<void>;
};

const EditableDocumentTitle: FC<{
  title: string;
  onChange: (title: string) => void;
}> = ({ title, onChange }) => {
  const { formatMessage } = useIntl();
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const element = titleRef.current;
    if (!element || document.activeElement === element) {
      return;
    }
    if (element.textContent !== title) {
      element.textContent = title;
    }
  }, [title]);

  return (
    <h1
      ref={titleRef}
      className={styles.documentTitle}
      aria-label={formatMessage(patientDocumentMessages.documentTitle)}
      aria-multiline="false"
      contentEditable
      data-placeholder={formatMessage(patientDocumentMessages.untitledDocument)}
      suppressContentEditableWarning
      onInput={(event) => onChange(event.currentTarget.textContent ?? '')}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      }}
      onPaste={(event) => {
        event.preventDefault();
        event.currentTarget.ownerDocument.execCommand(
          'insertText',
          false,
          event.clipboardData.getData('text/plain').replace(/[\r\n]+/g, ' '),
        );
      }}
    />
  );
};

const parseMarkdownTableRow = (line: string) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split(/(?<!\\)\|/)
    .map((cell) => cell.trim().replace(/\\\|/g, '|'));

const isMarkdownTableSeparator = (line: string) => {
  const cells = parseMarkdownTableRow(line);
  return (
    cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell.trim()))
  );
};

const MARKDOWN_TABLE_TRANSFORMER: MultilineElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: (node, traverseChildren) => {
    if (!$isTableNode(node)) {
      return null;
    }
    const rows = node.getChildren().filter($isTableRowNode);
    if (!rows.length) {
      return null;
    }
    const markdownRows = rows.map((row) => {
      const cells = row.getChildren().filter($isTableCellNode);
      const values = cells.map((cell) =>
        traverseChildren(cell)
          .trim()
          .replace(/\n+/g, '<br>')
          .replace(/\|/g, '\\|'),
      );
      return `| ${values.join(' | ')} |`;
    });
    const columnCount = rows[0].getChildren().filter($isTableCellNode).length;
    markdownRows.splice(
      1,
      0,
      `| ${Array.from({ length: columnCount }, () => '---').join(' | ')} |`,
    );
    return markdownRows.join('\n');
  },
  regExpStart: /^\s*\|.*\|\s*$/,
  handleImportAfterStartMatch: ({ lines, rootNode, startLineIndex }) => {
    if (
      startLineIndex + 1 >= lines.length ||
      !isMarkdownTableSeparator(lines[startLineIndex + 1])
    ) {
      return null;
    }

    const rowLines = [lines[startLineIndex]];
    let lastLineIndex = startLineIndex + 1;
    for (let index = startLineIndex + 2; index < lines.length; index += 1) {
      if (!/^\s*\|.*\|\s*$/.test(lines[index])) {
        break;
      }
      rowLines.push(lines[index]);
      lastLineIndex = index;
    }

    const table = $createTableNode();
    rowLines.forEach((line, rowIndex) => {
      const row = $createTableRowNode();
      parseMarkdownTableRow(line).forEach((value) => {
        const cell = $createTableCellNode(
          rowIndex === 0
            ? TableCellHeaderStates.ROW
            : TableCellHeaderStates.NO_STATUS,
        );
        const paragraph = $createParagraphNode();
        paragraph.append($createTextNode(value));
        cell.append(paragraph);
        row.append(cell);
      });
      table.append(row);
    });
    rootNode.append(table);
    return [true, lastLineIndex];
  },
  replace: () => undefined,
  type: 'multiline-element',
};

const MARKDOWN_TRANSFORMERS = [
  MARKDOWN_TABLE_TRANSFORMER,
  DOCUMENT_IMAGE_TRANSFORMER,
  ...TRANSFORMERS,
];

const InitialMarkdownPlugin: FC<{ value: string }> = ({ value }) => {
  const [editor] = useLexicalComposerContext();
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) {
      return;
    }
    editor.update(() => {
      $convertFromMarkdownString(value, MARKDOWN_TRANSFORMERS);
    });
    initialized.current = true;
  }, [editor, value]);

  return null;
};

const InsertImagePlugin: FC<{
  insertImageRef: MutableRefObject<InsertImage | null>;
  selectionRef: MutableRefObject<RangeSelection | null>;
}> = ({ insertImageRef, selectionRef }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    insertImageRef.current = (serial, onInserted) => {
      editor.update(
        () => {
          const imageNode = $createDocumentImageNode(serial);
          const currentSelection = $getSelection();
          const selection = $isRangeSelection(currentSelection)
            ? currentSelection
            : selectionRef.current?.clone();
          if ($isRangeSelection(selection)) {
            $setSelection(selection);
            selection.insertNodes([imageNode]);
            imageNode.selectNext();
            const nextSelection = $getSelection();
            selectionRef.current = $isRangeSelection(nextSelection)
              ? nextSelection.clone()
              : null;
            return;
          }
          const lastBlock = $getRoot().getLastChild();
          if (
            $isParagraphNode(lastBlock) &&
            lastBlock.getChildren().every($isDocumentImageNode)
          ) {
            lastBlock.append(imageNode);
            imageNode.selectNext();
            return;
          }
          const paragraph = $createParagraphNode();
          paragraph.append(imageNode);
          $getRoot().append(paragraph);
          imageNode.selectNext();
        },
        { onUpdate: onInserted },
      );
    };
    const unregisterSelection = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selectionRef.current = selection.clone();
        }
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
    return () => {
      unregisterSelection();
      insertImageRef.current = null;
    };
  }, [editor, insertImageRef, selectionRef]);

  return null;
};

const DocumentSelectionPlugin: FC<{
  replaceSelectionRef: MutableRefObject<EditSelection | null>;
  insertBelowSelectionRef: MutableRefObject<EditSelection | null>;
  onSelectionChange: (text: string) => void;
}> = ({ replaceSelectionRef, insertBelowSelectionRef, onSelectionChange }) => {
  const [editor] = useLexicalComposerContext();
  const lastSelectionRef = useRef<RangeSelection | null>(null);

  useEffect(() => {
    replaceSelectionRef.current = (text) => {
      let replaced = false;
      editor.update(() => {
        const currentSelection = $getSelection();
        const selection =
          $isRangeSelection(currentSelection) && !currentSelection.isCollapsed()
            ? currentSelection
            : lastSelectionRef.current?.clone();
        if (!$isRangeSelection(selection) || selection.isCollapsed()) {
          return;
        }
        $setSelection(selection);
        selection.insertText(text);
        lastSelectionRef.current = null;
        onSelectionChange('');
        replaced = true;
      });
      return replaced;
    };
    insertBelowSelectionRef.current = (text) => {
      let inserted = false;
      editor.update(() => {
        const currentSelection = $getSelection();
        const selection =
          $isRangeSelection(currentSelection) && !currentSelection.isCollapsed()
            ? currentSelection
            : lastSelectionRef.current?.clone();
        if (!$isRangeSelection(selection)) {
          return;
        }
        $setSelection(selection);
        const anchor = selection.focus.getNode().getTopLevelElement();
        if (!anchor) {
          return;
        }
        let insertionPoint = anchor;
        text
          .split(/\n{2,}/)
          .map((part) => part.trim())
          .filter(Boolean)
          .forEach((part) => {
            const paragraph = $createParagraphNode();
            paragraph.append($createTextNode(part));
            insertionPoint.insertAfter(paragraph);
            insertionPoint = paragraph;
          });
        lastSelectionRef.current = null;
        onSelectionChange('');
        inserted = true;
      });
      return inserted;
    };

    const unregisterSelectionState = editor.registerUpdateListener(
      ({ editorState }) => {
        editorState.read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection) && !selection.isCollapsed()) {
            lastSelectionRef.current = selection.clone();
          }
        });
      },
    );

    const unregisterSelection = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) && !selection.isCollapsed()) {
          lastSelectionRef.current = selection.clone();
          onSelectionChange(selection.getTextContent());
        } else if (editor.getRootElement()?.contains(document.activeElement)) {
          lastSelectionRef.current = null;
          onSelectionChange('');
        }
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );

    return () => {
      unregisterSelectionState();
      unregisterSelection();
      replaceSelectionRef.current = null;
      insertBelowSelectionRef.current = null;
    };
  }, [editor, insertBelowSelectionRef, onSelectionChange, replaceSelectionRef]);

  return null;
};

const Toolbar: FC<{
  onInsertImage: () => void;
  onUploadImage: () => void;
  imageSelectionRef: MutableRefObject<RangeSelection | null>;
}> = ({ onInsertImage, onUploadImage, imageSelectionRef }) => {
  const { formatMessage } = useIntl();
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [formats, setFormats] = useState({ bold: false, italic: false });
  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return;
          }
          const bold = selection.hasFormat('bold');
          const italic = selection.hasFormat('italic');
          setFormats((current) =>
            current.bold === bold && current.italic === italic
              ? current
              : { bold, italic },
          );
        });
      }),
    [editor],
  );

  useEffect(
    () =>
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (value) => {
          setCanUndo(value);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  useEffect(
    () =>
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (value) => {
          setCanRedo(value);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  const saveImageSelection = () => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        imageSelectionRef.current = selection.clone();
      }
    });
  };

  const setBlock = (type: 'paragraph' | 'h1' | 'h2' | 'h3') => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return;
      }
      $setBlocksType(selection, () =>
        type === 'paragraph'
          ? $createParagraphNode()
          : $createHeadingNode(type),
      );
    });
  };

  return (
    <div
      className={styles.toolbar}
      aria-label={formatMessage(patientDocumentMessages.documentFormatting)}
    >
      <div className={styles.toolGroup}>
        <button
          aria-label={formatMessage(patientDocumentMessages.undo)}
          title={formatMessage(patientDocumentMessages.undo)}
          type="button"
          disabled={!canUndo}
          onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
        >
          <Icon name="editorUndo" size={20} />
        </button>
        <button
          aria-label={formatMessage(patientDocumentMessages.redo)}
          title={formatMessage(patientDocumentMessages.redo)}
          type="button"
          disabled={!canRedo}
          onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
        >
          <Icon name="editorRedo" size={20} />
        </button>
      </div>
      <span className={styles.divider} aria-hidden="true" />
      <div className={styles.toolGroup}>
        <button
          aria-label={formatMessage(patientDocumentMessages.text)}
          title={formatMessage(patientDocumentMessages.text)}
          type="button"
          onClick={() => setBlock('paragraph')}
        >
          <Icon name="editorParagraph" size={20} />
        </button>
        <button
          aria-label={formatMessage(patientDocumentMessages.mainHeading)}
          title={formatMessage(patientDocumentMessages.mainHeading)}
          type="button"
          onClick={() => setBlock('h1')}
        >
          <Icon name="editorTitle" size={20} />
        </button>
        <button
          aria-label={formatMessage(patientDocumentMessages.heading)}
          title={formatMessage(patientDocumentMessages.heading)}
          type="button"
          onClick={() => setBlock('h2')}
        >
          <Icon name="editorHeading" size={20} />
        </button>
        <button
          aria-label={formatMessage(patientDocumentMessages.subheading)}
          title={formatMessage(patientDocumentMessages.subheading)}
          type="button"
          onClick={() => setBlock('h3')}
        >
          <Icon name="editorSubheading" size={20} />
        </button>
      </div>
      <span className={styles.divider} aria-hidden="true" />
      <div className={styles.toolGroup}>
        <button
          aria-pressed={formats.bold}
          aria-label={formatMessage(patientDocumentMessages.bold)}
          title={formatMessage(patientDocumentMessages.bold)}
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        >
          <Icon name="editorBold" size={20} />
        </button>
        <button
          aria-pressed={formats.italic}
          aria-label={formatMessage(patientDocumentMessages.italic)}
          title={formatMessage(patientDocumentMessages.italic)}
          type="button"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
        >
          <Icon name="editorItalic" size={20} />
        </button>
        <button
          aria-label={formatMessage(patientDocumentMessages.bullets)}
          title={formatMessage(patientDocumentMessages.bullets)}
          type="button"
          onClick={() =>
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
          }
        >
          <Icon name="editorBullets" size={20} />
        </button>
        <button
          aria-label={formatMessage(patientDocumentMessages.numbered)}
          title={formatMessage(patientDocumentMessages.numbered)}
          type="button"
          onClick={() =>
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
          }
        >
          <Icon name="editorNumbered" size={20} />
        </button>
      </div>
      <div className={styles.toolbarActions}>
        <button
          aria-label={formatMessage(patientDocumentMessages.uploadImage)}
          title={formatMessage(patientDocumentMessages.uploadImage)}
          type="button"
          className={styles.toolbarAction}
          onMouseDown={(event) => {
            event.preventDefault();
            saveImageSelection();
          }}
          onClick={onUploadImage}
        >
          <Icon name="editorUploadImage" size={20} />
        </button>
        <button
          aria-label={formatMessage(patientDocumentMessages.insertReportImage)}
          title={formatMessage(patientDocumentMessages.insertReportImage)}
          type="button"
          className={styles.toolbarAction}
          onMouseDown={(event) => {
            event.preventDefault();
            saveImageSelection();
          }}
          onClick={onInsertImage}
        >
          <Icon name="editorReportImage" size={20} />
        </button>
      </div>
    </div>
  );
};

export const MarkdownWysiwygEditor: FC<Props> = ({
  title,
  onTitleChange,
  value,
  documentID,
  images,
  resolveAsset,
  insertImageRef,
  replaceSelectionRef,
  insertBelowSelectionRef,
  onSelectionChange,
  onChange,
  onInsertImage,
  onUploadImage,
  onUpdateImage,
}) => {
  const { formatMessage } = useIntl();
  const initialConfig = {
    namespace: 'PatientDocumentMarkdownEditor',
    theme: {
      root: styles.editor,
      paragraph: styles.paragraph,
      heading: {
        h1: styles.mainHeading,
        h2: styles.heading,
        h3: styles.subheading,
      },
      list: {
        ul: styles.list,
        ol: styles.list,
        listitem: styles.listItem,
      },
      text: {
        bold: styles.bold,
        italic: styles.italic,
        code: styles.code,
      },
      table: styles.table,
      tableCell: styles.tableCell,
      tableCellHeader: styles.tableCellHeader,
      tableScrollableWrapper: styles.tableScroll,
    },
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      LinkNode,
      CodeNode,
      TableNode,
      TableRowNode,
      TableCellNode,
      DocumentImageNode,
    ],
    onError: (error: Error) => {
      throw error;
    },
  };

  const handleChange = useCallback(
    (
      editorState: Parameters<
        NonNullable<React.ComponentProps<typeof OnChangePlugin>['onChange']>
      >[0],
    ) => {
      editorState.read(() => {
        onChange($convertToMarkdownString(MARKDOWN_TRANSFORMERS, $getRoot()));
      });
    },
    [onChange],
  );

  const assetContext = useMemo(
    () => ({
      documentID,
      images: new Map(images.map((image) => [image.ID, image])),
      resolveAsset,
      updateImage: onUpdateImage ?? (() => undefined),
    }),
    [documentID, images, resolveAsset, onUpdateImage],
  );
  const imageSelectionRef = useRef<RangeSelection | null>(null);

  return (
    <DocumentAssetContext.Provider value={assetContext}>
      <LexicalComposer initialConfig={initialConfig}>
        <div className={styles.shell}>
          <Toolbar
            onInsertImage={onInsertImage}
            onUploadImage={onUploadImage}
            imageSelectionRef={imageSelectionRef}
          />
          <div className={styles.canvas}>
            <div className={styles.paper}>
              <EditableDocumentTitle title={title} onChange={onTitleChange} />
              <div className={styles.bodyRegion}>
                <RichTextPlugin
                  contentEditable={
                    <ContentEditable
                      className={styles.editor}
                      aria-label={formatMessage(
                        patientDocumentMessages.documentContent,
                      )}
                    />
                  }
                  placeholder={
                    <span className={styles.placeholder}>
                      {formatMessage(patientDocumentMessages.startWriting)}
                    </span>
                  }
                  ErrorBoundary={LexicalErrorBoundary}
                />
              </div>
            </div>
          </div>
          <HistoryPlugin />
          <ListPlugin />
          <TablePlugin hasCellMerge={false} hasHorizontalScroll />
          <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
          <InitialMarkdownPlugin value={value} />
          <InsertImagePlugin
            insertImageRef={insertImageRef}
            selectionRef={imageSelectionRef}
          />
          <DocumentSelectionPlugin
            replaceSelectionRef={replaceSelectionRef}
            insertBelowSelectionRef={insertBelowSelectionRef}
            onSelectionChange={onSelectionChange}
          />
        </div>
      </LexicalComposer>
    </DocumentAssetContext.Provider>
  );
};
